import React, { useState, useEffect, useRef } from 'react';
import './PrivacyModeDropdown.css';

const PrivacyModeDropdown = ({ user, profile, supabaseIntegration, onPrivacyModeChange, displayMode = 'dropdown' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPrivacyMode, setCurrentPrivacyMode] = useState('public');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Initialize privacy mode from profile or settings
  useEffect(() => {
    if (user && supabaseIntegration?.supabaseHelpers) {
      loadUserPrivacyMode();
    }
  }, [user, supabaseIntegration]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadUserPrivacyMode = async () => {
    try {
      const { privacy_mode } = await supabaseIntegration.supabaseHelpers.getUserPrivacyMode(user.sub);
      setCurrentPrivacyMode(privacy_mode);
    } catch (error) {
      console.error('Error loading privacy mode:', error);
      setCurrentPrivacyMode('public'); // fallback
    }
  };

  const handlePrivacyModeChange = async (newMode) => {
    if (newMode === currentPrivacyMode || loading) {
      return;
    }

    setLoading(true);
    try {
      // Update user's global privacy mode
      const { error } = await supabaseIntegration.supabaseHelpers.updateUserPrivacyMode(user.sub, newMode);

      if (error) {
        throw new Error(error.message || 'Failed to update privacy mode');
      }

      setCurrentPrivacyMode(newMode);
      setIsOpen(false);

      // Notify parent component
      if (onPrivacyModeChange) {
        onPrivacyModeChange(newMode);
      }

      // Log the change
      if (supabaseIntegration.logUserEvent) {
        supabaseIntegration.logUserEvent('privacy_mode_changed', {
          old_mode: currentPrivacyMode,
          new_mode: newMode,
          timestamp: new Date().toISOString()
        });
      }

    } catch (error) {
      console.error('Error updating privacy mode:', error);
      // Could add toast notification here
    } finally {
      setLoading(false);
    }
  };

  const getPrivacyIcon = (mode) => {
    switch (mode) {
      case 'public': 
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
        );
      case 'all_friends': 
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        );
      default: 
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <circle cx="12" cy="16" r="1"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        );
    }
  };

  const getPrivacyLabel = (mode) => {
    switch (mode) {
      case 'public': return 'Public';
      case 'all_friends': return 'Friends';
      default: return 'Private';
    }
  };

  const getPrivacyDescription = (mode) => {
    switch (mode) {
      case 'public': 
        return 'Everyone can see your location interests';
      case 'all_friends': 
        return 'Only your friends can see your location interests';
      default: 
        return 'Your location interests are private';
    }
  };

  const privacyOptions = [
    {
      mode: 'public',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      ),
      label: 'Public',
      description: 'Everyone can see your interests'
    },
    {
      mode: 'all_friends',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      label: 'Friends',
      description: 'Only friends can see your interests'
    }
  ];

  // Radio button mode rendering
  if (displayMode === 'radio') {
    return (
      <div className="privacy-mode-radio-group">
        {privacyOptions.map((option) => (
          <label key={option.mode} className={`privacy-radio-option ${currentPrivacyMode === option.mode ? 'active' : ''}`}>
            <input
              type="radio"
              name="privacy-mode"
              value={option.mode}
              checked={currentPrivacyMode === option.mode}
              onChange={() => handlePrivacyModeChange(option.mode)}
              disabled={loading}
              className="privacy-radio-input"
            />
            <div className="privacy-radio-content">
              <div className="privacy-radio-icon">{option.icon}</div>
              <div className="privacy-radio-text">
                <div className="privacy-radio-label">{option.label}</div>
                <div className="privacy-radio-description">{option.description}</div>
              </div>
            </div>
          </label>
        ))}
        
        <div className="privacy-radio-note">
          <span className="privacy-note-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          </span>
          <span>This setting applies to ALL your location interests</span>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-mode-dropdown" ref={dropdownRef}>
      <button
        className={`privacy-mode-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        title={`Privacy: ${getPrivacyLabel(currentPrivacyMode)}`}
      >
        <span className="privacy-icon">{getPrivacyIcon(currentPrivacyMode)}</span>
        {isOpen && <span className="privacy-label">{getPrivacyLabel(currentPrivacyMode)}</span>}
        <svg 
          className={`privacy-arrow ${isOpen ? 'open' : ''}`} 
          width="12" 
          height="8" 
          viewBox="0 0 12 8"
        >
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      </button>

      {isOpen && (
        <div className="privacy-mode-menu">
          <div className="privacy-mode-header">
            <h4>Privacy Settings</h4>
            <p>Control who can see your location interests</p>
          </div>
          
          <div className="privacy-mode-options">
            {privacyOptions.map((option) => (
              <button
                key={option.mode}
                className={`privacy-mode-option ${currentPrivacyMode === option.mode ? 'active' : ''}`}
                onClick={() => handlePrivacyModeChange(option.mode)}
                disabled={loading}
              >
                <div className="privacy-option-icon">{option.icon}</div>
                <div className="privacy-option-content">
                  <div className="privacy-option-label">{option.label}</div>
                  <div className="privacy-option-description">{option.description}</div>
                </div>
                {currentPrivacyMode === option.mode && (
                  <div className="privacy-option-check">✓</div>
                )}
              </button>
            ))}
          </div>

          <div className="privacy-mode-note">
            <span className="privacy-note-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </span>
            <span>This setting applies to ALL your location interests</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyModeDropdown; 